import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { notification } from 'antd';

import DaysOfWeekLabels from '@src/components/DaysOfWeekLabels';
import type { ResponsiveTableColumns } from '@src/components/Table';
import { COLUMN_TYPES, RESPONSIVE_COLUMN_TYPE } from '@src/components/Table/constants';
import { CommonReduxState } from '@src/models/commons/reduxState.interface';
import WorkJourneyState from '@src/packages/workJourney/components/WorkJourneyState';
import { getRouteToWorkJourneyMonthMatrix, PATHS } from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyForm } from '@src/packages/workJourney/types';
import { getFullUrl } from '@src/packages/utils/functions';
import { DateUtil } from '@src/packages/utils/dates';
import WorkJourneyService from '@src/services/WorkJourneyService';
import safeExecute from '@src/util/safeExecute';

import WorkJourneyActions from '../components/WorkJourneyActions';
import WorkJourneyUsers from '../components/WorkJourneyUsers';

const workJourneyService = new WorkJourneyService();

const getCurrentMonthYear = () => {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
};

export const useWorkJourney = () => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const intl = useIntl();
  const organizationId = useSelector(
    ({ organizations }: CommonReduxState) => organizations.organization.id,
  );
  const { setRefreshTrigger, setSelectedWorkJourney } = useWorkJourneyContext();

  const handleEdit = useCallback(
    (record: WorkJourneyForm) => {
      history.push(PATHS.EDIT_WORK_JOURNEY.replace(':id', record.id as string));
    },
    [history],
  );

  const handleDelete = useCallback(
    async (record: WorkJourneyForm) => {
      await safeExecute(
        async () => {
          await workJourneyService.delete(record.id as string, organizationId);
          setRefreshTrigger(prev => prev + 1);
          notification.success({
            message: intl.formatMessage({ id: 'workJourney.delete.success' }),
          });
        },
        'WorkJourneyService.delete',
        { workJourneyId: record.id, organizationId },
      );
    },
    [intl, organizationId, setRefreshTrigger],
  );

  const handleViewDetails = useCallback(
    (_record: WorkJourneyForm) => {
      notification.info({ message: intl.formatMessage({ id: 'workJourney.soon' }) });
    },
    [intl],
  );

  const handleViewInfo = useCallback(
    (record: WorkJourneyForm) => {
      setSelectedWorkJourney(record);
    },
    [setSelectedWorkJourney],
  );

  const handleOpenMatrix = useCallback(
    (record: WorkJourneyForm) => {
      if (!record.id) return;

      const { month, year } = getCurrentMonthYear();
      history.push(
        getFullUrl(
          getRouteToWorkJourneyMonthMatrix({
            workJourneyConfigId: record.id,
            year,
            month,
          }),
          url,
        ),
      );
    },
    [history, url],
  );

  const columns = useMemo(
    () =>
      [
        {
          key: 'name',
          title: intl.formatMessage({ id: 'workJourney.columns.journey' }),
          dataIndex: 'name',
          align: 'left' as const,
          render: (record: WorkJourneyForm) => <>{record.name}</>,
        },
        {
          key: 'workShift',
          title: intl.formatMessage({ id: 'workJourney.columns.shift' }),
          dataIndex: 'workShift',
          align: 'left' as const,
          sortable: true,
          sortConfig: { type: COLUMN_TYPES.NESTED, path: 'name' },
          render: (record: WorkJourneyForm) => <>{record.workShift?.name}</>,
        },
        {
          key: 'daysOfWeek',
          title: intl.formatMessage({ id: 'workJourney.columns.days' }),
          dataIndex: 'workShift',
          align: 'left' as const,
          render: (record: WorkJourneyForm) => (
            <DaysOfWeekLabels days={record.workShift?.daysOfWeek} />
          ),
        },
        {
          key: 'users',
          title: intl.formatMessage({ id: 'workJourney.columns.users' }),
          dataIndex: 'members',
          align: 'left' as const,
          width: 100,
          sortable: true,
          sortConfig: { type: COLUMN_TYPES.ARRAY_LENGTH },
          render: (record: WorkJourneyForm) => <WorkJourneyUsers record={record} />,
        },
        {
          key: 'state',
          title: intl.formatMessage({ id: 'workJourney.columns.state' }),
          dataIndex: 'state',
          align: 'left' as const,
          width: 160,
          sortable: true,
          sortConfig: { type: COLUMN_TYPES.STRING },
          render: (record: WorkJourneyForm) => <WorkJourneyState workJourney={record} />,
        },
        {
          key: 'updatedAt',
          title: intl.formatMessage({ id: 'general.updatedAt' }),
          dataIndex: 'updatedAt',
          align: 'left' as const,
          width: 120,
          sortable: true,
          sortConfig: { type: COLUMN_TYPES.DATE },
          render: (record: WorkJourneyForm) => (
            <>{DateUtil.convertTimestampToDate(record.updatedAt, 'DD/MM/YYYY HH:mm')}</>
          ),
        },
        {
          key: 'actions',
          title: '',
          dataIndex: 'actions',
          align: 'left' as const,
          columnType: RESPONSIVE_COLUMN_TYPE.ACTIONS,
          actionsLayout: 'end',
          width: 72,
          render: (record: WorkJourneyForm) => (
            <WorkJourneyActions
              record={record}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewInfo={handleViewInfo}
              onOpenMatrix={handleOpenMatrix}
            />
          ),
        },
      ] as ResponsiveTableColumns<WorkJourneyForm>,
    [intl, handleEdit, handleDelete, handleViewInfo, handleOpenMatrix],
  );

  return {
    columns,
    handleEdit,
    handleDelete,
    handleViewDetails,
    handleViewInfo,
    handleOpenMatrix,
  };
};
